import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, CalendarClock, Send, CheckCircle2, Video, Lock, TrendingDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate, fmtDateTime, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, PageLoader, Panel, StatCard, StatusBadge, DemoBanner } from "@/components/common";
import { TutorBrowser } from "@/components/TutorBrowser";
import { TxTable } from "@/pages/admin/CreditsSettings";
import { WeeklyCalendar } from "@/components/WeeklyCalendar";
import { ViewToggle } from "@/components/ViewToggle";

async function joinSession(s, refresh) {
  const win = window.open("", "_blank");
  try {
    const { data } = await api.post(`/sessions/${s.id}/join`);
    if (win) win.location.href = data.join_url; else window.location.href = data.join_url;
    toast.success("Joining live session — attendance recorded");
    refresh?.();
  } catch (e) {
    win?.close();
    toast.error(errMsg(e));
  }
}

function SessionRows({ items, refresh }) {
  return (
    <div className="overflow-x-auto">
      <Table data-testid="student-sessions-table">
        <TableHeader><TableRow><TableHead>Session</TableHead><TableHead>Tutor</TableHead><TableHead>Date</TableHead><TableHead>Time</TableHead>
          <TableHead>Status</TableHead><TableHead>Credits</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {items.map((s) => (
            <TableRow key={s.id} data-testid={`student-session-row-${s.id}`}>
              <TableCell><p className="font-semibold text-[#0B1A33]">{s.title}</p><p className="text-xs text-slate-500">{s.subject}</p></TableCell>
              <TableCell>{s.tutor_name}</TableCell>
              <TableCell className="whitespace-nowrap">{fmtDate(s.start_at)}</TableCell>
              <TableCell className="whitespace-nowrap">{fmtTime(s.start_at)} · {s.duration}m</TableCell>
              <TableCell><StatusBadge status={s.status} testid={`student-session-status-${s.id}`} /></TableCell>
              <TableCell><StatusBadge status={s.my_participation?.credit_status} /></TableCell>
              <TableCell>
                {["scheduled", "in_progress"].includes(s.status) && (
                  <Button size="sm" disabled={!s.can_join} onClick={() => joinSession(s, refresh)} data-testid={`join-session-button-${s.id}`}
                    title={s.can_join ? "" : "Available 15 minutes before start"} className="rounded-full bg-[#3CC34A] font-semibold text-[#0B1A33] hover:bg-[#2DA638]">
                    <Video className="h-4 w-4" />Join
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function StudentDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/student/dashboard").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <PageLoader />;
  return (
    <div>
      <PageHeader title={`Hi, ${user.name.split(" ")[0]}`} subtitle="Here's your learning at a glance."
        actions={<Link to="/student/find-tutor" className="rounded-full bg-[#1F6FFF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1450C9]" data-testid="dashboard-find-tutor">Find a tutor</Link>} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        <StatCard label="Available Credits" value={d.wallet.available} icon={Wallet} tone="navy" testid="student-dashboard-credit-balance" hint={`${d.wallet.reserved} reserved`} />
        <StatCard label="Upcoming Sessions" value={d.upcoming_count} icon={CalendarClock} testid="student-stat-upcoming" />
        <StatCard label="Pending Tutor Requests" value={d.pending_requests} icon={Send} tone="amber" testid="student-stat-pending" />
        <StatCard label="Completed Sessions" value={d.completed_count} icon={CheckCircle2} tone="green" testid="student-stat-completed" />
      </div>
      {d.wallet.available < 100 && <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="low-credit-warning">Your balance is below 100 credits. Contact the FosterApp admin to top up before your next session.</div>}
      <Panel className="mt-8">
        <div className="flex items-center justify-between p-6 pb-2"><h3 className="text-lg font-bold text-[#0B1A33]">Upcoming sessions</h3>
          <Link to="/student/sessions" className="text-sm font-semibold text-[#1F6FFF]">View all</Link></div>
        {d.upcoming.length ? <SessionRows items={d.upcoming} refresh={load} /> : <div className="p-6"><EmptyState title="No upcoming sessions" text="Request a tutor to get started." /></div>}
      </Panel>
    </div>
  );
}

export function StudentFindTutor() {
  return (<div><PageHeader title="Find Tutor" subtitle="Search verified tutors by name, subject, specialization and experience." /><TutorBrowser profileBase="/student/tutors" /></div>);
}

export function StudentRequests() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  useEffect(() => { api.get("/student/requests", { params: { status: status === "all" ? "" : status } }).then((r) => setItems(r.data)); }, [status]);
  return (
    <div>
      <PageHeader title="My Requests" subtitle="Track the coaching requests you've sent." />
      <Tabs value={status} onValueChange={setStatus} className="mb-4"><TabsList>
        {["all", "pending", "approved", "rejected"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`requests-tab-${s}`}>{s}</TabsTrigger>)}
      </TabsList></Tabs>
      <Panel>
        {!items ? <PageLoader /> : items.length === 0 ? <div className="p-6"><EmptyState title="No requests" action={<Link to="/student/find-tutor" className="text-sm font-semibold text-[#1F6FFF]">Find a tutor</Link>} /></div> : (
          <Table data-testid="student-requests-table">
            <TableHeader><TableRow><TableHead>Tutor</TableHead><TableHead>Subject</TableHead><TableHead>Message</TableHead><TableHead>Sent</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{items.map((r) => (
              <TableRow key={r.id} data-testid={`student-request-row-${r.id}`}>
                <TableCell className="font-semibold"><Link to={`/student/tutors/${r.tutor_id}`} className="hover:text-[#1F6FFF]">{r.tutor_name}</Link></TableCell>
                <TableCell>{r.subject}</TableCell><TableCell className="max-w-xs truncate text-sm text-slate-500">{r.message || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDateTime(r.created_at)}</TableCell>
                <TableCell><StatusBadge status={r.status} testid={`student-request-status-${r.id}`} /></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </Panel>
    </div>
  );
}

export function StudentSessions() {
  const [scope, setScope] = useState("upcoming");
  const [view, setView] = useState("calendar");
  const [items, setItems] = useState(null);
  const [cfg, setCfg] = useState(null);
  const load = useCallback(() => api.get("/student/sessions", { params: { scope } }).then((r) => setItems(r.data)), [scope]);
  useEffect(() => { setItems(null); load(); }, [load]);
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  return (
    <div>
      <PageHeader title="My Sessions" subtitle="Join live classes from here. The Join button activates 15 minutes before start." />
      {cfg?.video_provider === "jitsi" && <div className="mb-4"><DemoBanner text="Live video runs on free Jitsi Meet rooms (not Zoom) until Zoom credentials are configured" /></div>}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={scope} onValueChange={setScope}><TabsList>
          <TabsTrigger value="upcoming" data-testid="sessions-tab-upcoming">Upcoming</TabsTrigger><TabsTrigger value="history" data-testid="sessions-tab-history">History</TabsTrigger>
        </TabsList></Tabs>
        {scope === "upcoming" && <ViewToggle view={view} setView={setView} />}
      </div>
      <Panel>{!items ? <PageLoader /> : scope === "upcoming" && view === "calendar" ? (
        <WeeklyCalendar items={items} subtitle={(s) => `with ${s.tutor_name}`} renderAction={(s) => (
          <Button size="sm" disabled={!s.can_join} onClick={() => joinSession(s, load)} data-testid={`calendar-join-button-${s.id}`}
            className="h-6 rounded-full bg-[#3CC34A] px-2.5 text-[11px] font-semibold text-[#0B1A33] hover:bg-[#2DA638]"><Video className="h-3 w-3" />Join</Button>
        )} />
      ) : items.length === 0 ? <div className="p-6"><EmptyState title="No sessions here yet" /></div> : <SessionRows items={items} refresh={load} />}</Panel>
    </div>
  );
}

export function StudentWallet() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/student/wallet").then((r) => setD(r.data)); }, []);
  if (!d) return <PageLoader />;
  return (
    <div>
      <PageHeader title="Credit Wallet" subtitle="Credits are managed by the FosterApp admin. Each completed session costs 100 credits." />
      <div className="grid gap-4 md:grid-cols-3">
        <div className="grain relative overflow-hidden rounded-2xl bg-[#0B1A33] p-6 text-white md:row-span-1" data-testid="wallet-available">
          <div className="grid-texture absolute inset-0" />
          <p className="relative text-xs font-semibold uppercase tracking-wider text-slate-400">Available credits</p>
          <p className="font-display relative mt-3 text-5xl font-extrabold text-[#3CC34A]" data-testid="wallet-available-value">{d.wallet.available}</p>
          <p className="relative mt-2 text-sm text-slate-400">≈ {Math.floor(d.wallet.available / 100)} session(s)</p>
        </div>
        <StatCard label="Reserved Credits" value={d.wallet.reserved} icon={Lock} tone="amber" testid="wallet-reserved" />
        <StatCard label="Credits Used" value={d.wallet.used} icon={TrendingDown} tone="green" testid="wallet-used" />
      </div>
      <Panel className="mt-8">
        <h3 className="p-6 pb-2 text-lg font-bold text-[#0B1A33]">Transaction history</h3>
        {d.transactions.length ? <div className="overflow-x-auto"><TxTable admin={false} items={d.transactions} /></div> : <div className="p-6"><EmptyState title="No transactions yet" /></div>}
      </Panel>
    </div>
  );
}
